import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  C,
  T,
  O,
  Masks,
  Sum,
  Flatten,
  FindPerguntaByCode,
  roundToDecimals,
} from 'shared/helpers';
import moment from 'moment';

import Styles from './ExpectativaMínima.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons';

import { Creators as reduxPems } from 'store/ducks/pems';
import { Creators as reduxEtapas } from 'store/ducks/etapas';
import { Creators as reduxProdutos } from 'store/ducks/produtos';
import { Creators as reduxPerguntas } from 'store/ducks/perguntas';
import { Creators as reduxCulturas } from 'store/ducks/culturas';
import { Creators as reduxEstados } from 'store/ducks/estados';
import { Creators as reduxRespostas } from 'store/ducks/respostas';
import { Creators as reduxCidades } from 'store/ducks/cidades';

import Header from 'components/Header/Header';
import TopTable from './TopTable/TopTable';
import BottomTable from './BottomTable/BottomTable';

import { useEditableContext } from 'shared/hooks';
import PrintButton from 'components/PrintButton/PrintButton';
import WithPermission from 'components/Template/WithPermission/WithPermission';

export default function ExpectativaMínima({ match }) {
  const { ExpectativaMínima, Selected, Saver, Hidden } = Styles;
  const { id } = match.params;
  const { number, currency } = Masks;
  const dispatch = useDispatch();

  const [edit, setEdit] = useState(false);
  const block = useRef(false);

  const Questions = useSelector((state) => state.perguntas.data || []);
  const QuestionsMap = useSelector((state) => state.perguntas.map || {});
  const Perguntas = Questions.filter((x) => x.fase_id === 8);
  const Savings = Questions.filter((x) => x.fase_id === 7);

  const [init, setInit] = useState(false);

  useEditableContext(edit);

  useEffect(() => {
    if (!init) {
      dispatch(
        reduxPerguntas.getPerguntas((x) => x.fase_id === 7 || x.fase_id === 8),
      );
      setInit(true);
    }
  }, [id, init, dispatch]);

  useEffect(() => {
    dispatch(
      reduxRespostas.getRespostas((x) => String(x.pem_id) === String(id)),
    );
  }, [id, dispatch, Questions]);

  const Pems = useSelector((state) => state.pems.data);
  const Etapas = useSelector((state) => state.etapas.data);
  const Culturas = useSelector((state) => state.culturas.data);
  const Produtos = useSelector((state) => state.produtos.data);
  const Estados = useSelector((state) => state.estados.data);
  const Respostas = useSelector((state) => state.respostas.data || []);
  const Pem = Pems.find((x) => String(x.id) === String(id));
  const Cultura = T(Culturas).find((x) => x.id === T(Pem).cultura_id);
  const User = useSelector((state) => state.user.data);
  const Cidades = useSelector((state) => state.cidades.data);
  const PEMEtapa = useSelector((state) => state.etapas.pem);

  useEffect(() => {
    if (!init) {
      dispatch(reduxPems.getPems());
      dispatch(reduxProdutos.getProdutos());
      dispatch(reduxCulturas.getCulturas());
      dispatch(reduxEtapas.getEtapas(id));
      dispatch(reduxEstados.getEstados());
    }
    if (Estados instanceof Array && Estados.length) {
      dispatch(
        reduxCidades.getCidades(
          (x) => String(x.estado_id) === String(T(Pem).estado_id),
        ),
      );
    }
  }, [id, dispatch, init, Estados, Pem]);

  const save = () => {
    if (block.current) return;
    block.current = true;
    dispatch(
      reduxRespostas.saveRespostas(
        id,
        8,
        { pergunta: form },
        (x) => String(x.pem_id) === String(id),
      ),
    );
    setTimeout(() => setEdit(false), 2000);
  };
  useEffect(() => {
    if (edit) block.current = false;
  }, [edit]);
  const [form, setForm] = useState(
    O(
      T(Perguntas, []).map((p) => p.id),
      [],
      { status: [[], []] },
    ),
  );
  const { id: receitaId } = useMemo(() => FindPerguntaByCode(Questions, 'receita_bruta'), [Questions]);

  useEffect(() => {
    setForm(form => {
      const existingRespostas = T(
        T(Questions, []).filter((x) => x.fase_id === 8),
        [],
      ).map(p => T(T(Respostas, []).find(x => String(x.pergunta_id) === String(p.id))).resposta);
      const idsPergunta = T(
        T(Questions, []).filter((x) => x.fase_id === 8),
        [],
      ).map((pergunta) => pergunta.id);

      const defaultValues = idsPergunta.map(idPergunta => {
        const pergunta = QuestionsMap[idPergunta];
        if (!pergunta) {
          return '';
        }
        if (pergunta.attributes && pergunta.attributes.default) {
          return pergunta.attributes.default;
        }
        if (pergunta.code === 'custoTotalProducao') {
          return form[receitaId];
        }
        if (pergunta.code === 'nomeSQMVitas') {
          return T(User).name;
        }
        if (pergunta.code === 'nomeProdutorRural') {
          return T(Pem).produtor;
        }
        if (pergunta.code === 'estado') {
          return T(
            T(Estados, []).find(
              (x) => String(x.id) === String(T(Pem).estado_id),
            ),
          ).nome;
        }
        if (pergunta.code === 'municipio') {
          return T(
            T(Cidades, []).find(
              (x) => String(x.id) === String(T(Pem).cidade_id),
            ),
          ).nome;
        }
        if (pergunta.attributes && pergunta.attributes.inputGroup === 'ha') {
          return 0;
        }
        if (pergunta.type === 'select' || pergunta.type === 'text') {
          return '';
        }
        if (pergunta.type === 'date') {
          return moment().format('YYYY-MM-DD');
        }
        if (pergunta.type === 'radio') {
          return { 0: true, 1: false };
        }
        return '';
      });
      return O(
        idsPergunta,
        existingRespostas,
        defaultValues,
      );
    });
  }, [Questions, Respostas, Cidades, Estados, Pem, User, edit, receitaId, QuestionsMap]);

  const [
    { id: prodId },
    { id: precoId },
  ] = [
      FindPerguntaByCode(Perguntas, 'produtividadeHa'),
      FindPerguntaByCode(Perguntas, 'precoUnCultura'),
    ];

  const precoUnidade = form[precoId];
  const produtividade = form[prodId];
  useEffect(() => {
    setForm(form => ({
      ...form,
      [receitaId]: (produtividade && precoUnidade)
        ? (produtividade * precoUnidade).toFixed(2)
        : undefined,
    }));
  }, [receitaId, precoUnidade, produtividade]);

  if (PEMEtapa !== id) {
    return null;
  }

  const EPs = Flatten(
    Etapas.map((etapa) =>
      etapa.produtos.map((produto) => ({
        ...Produtos.find((x) => x.id === produto.id),
        ...produto,
      })),
    ),
  );

  const appliedProductTotals = [
    Sum(EPs.filter((x) => x.fase_id === 2).map((y) => Number(y.quantidade))),
    Sum(EPs.filter((x) => x.fase_id === 3).map((y) => Number(y.quantidade))),
  ];

  const fertilizationCostSQM = Sum(
    EPs.filter((x) => x.fase_id === 2).map(
      (x) => (Number(x.valor) / 1000) * Number(x.quantidade),
    ),
  );
  const fertilizationCostConv = Sum(
    EPs.filter((x) => x.fase_id === 3).map(
      (x) => (Number(x.valor) / 1000) * Number(x.quantidade),
    ),
  );
  const fertilizationCostDiff = fertilizationCostSQM - fertilizationCostConv;

  const fertilizationCostTotals = [
    fertilizationCostSQM,
    fertilizationCostConv,
    fertilizationCostDiff,
    (fertilizationCostDiff / fertilizationCostConv) * 100,
  ].map((n) => roundToDecimals(n));

  if (
    !FindPerguntaByCode(Perguntas, 'areaTotal') ||
    !FindPerguntaByCode(Perguntas, 'areaTotal').id
  ) {
    return null;
  }

  const puid = T(
    T(Perguntas, []).find((x) => x.code === 'unidadeProducao'),
    {},
  ).id;

  const totalArea =
    T(
      T(Respostas, []).find(
        (x) =>
          String(x.pergunta_id) ===
          String(FindPerguntaByCode(Perguntas, 'areaTotal').id),
      ),
    ).resposta || 0;

  const costPerUnitOfProduction =
    Number(
      T(
        T(Respostas, []).find(
          (x) =>
            String(x.pergunta_id) ===
            String(FindPerguntaByCode(Perguntas, 'precoUnCultura').id),
        ),
      ).resposta,
    ) || 1;

  const haProductivity =
    T(
      T(Respostas, []).find(
        (x) =>
          String(x.pergunta_id) ===
          String(FindPerguntaByCode(Perguntas, 'produtividadeHa').id),
      ),
    ).resposta || 0;

  const totalProductionCost =
    T(
      T(Respostas, []).find(
        (x) =>
          String(x.pergunta_id) ===
          String(FindPerguntaByCode(Perguntas, 'custoTotalProducao').id),
      ),
    ).resposta || 0;

  const fertilizationCostTotalsPerUnitOfProduction = [
    fertilizationCostTotals[0] / costPerUnitOfProduction,
    fertilizationCostTotals[1] / costPerUnitOfProduction,
    roundToDecimals(fertilizationCostTotals[0] / costPerUnitOfProduction) -
    roundToDecimals(fertilizationCostTotals[1] / costPerUnitOfProduction),
    haProductivity > 0
      ? (roundToDecimals(fertilizationCostTotals[0] / costPerUnitOfProduction) -
        roundToDecimals(
          fertilizationCostTotals[1] / costPerUnitOfProduction,
        )) /
      haProductivity
      : 0,
    (roundToDecimals(fertilizationCostTotals[0] / costPerUnitOfProduction) -
      roundToDecimals(fertilizationCostTotals[1] / costPerUnitOfProduction)) *
    totalArea || 0,
  ].map((n) => roundToDecimals(n));

  const totalSavings = Sum(
    Respostas.filter((x) =>
      Savings.map((y) => y.id).includes(x.pergunta_id),
    ).map((z) => T(z).resposta || 0),
  );

  const indirectSavings = [
    totalSavings || 0,
    0,
    totalSavings || 0,
    roundToDecimals(totalSavings / fertilizationCostTotals[0]) * 100 || 0,
    totalSavings * totalArea || 0,
  ];

  const indirectSavingsPerUnitOfProduction = [
    costPerUnitOfProduction > 0
      ? roundToDecimals(totalSavings / costPerUnitOfProduction)
      : 0 || 0,
    0,
    roundToDecimals(totalSavings / costPerUnitOfProduction) || 0,
    roundToDecimals(
      (totalSavings /
        roundToDecimals(
          costPerUnitOfProduction /
          T(
            Respostas.find(
              ({ pergunta_id: idResposta }) =>
                idResposta ===
                T(T(Perguntas).find(({ code }) => code === 'produtividadeHa'))
                  .id,
            ),
          ).resposta,
        )) *
      100 || 0,
    ),
    roundToDecimals(totalSavings / costPerUnitOfProduction) * totalArea || 0,
  ];

  const finalCostDiffPerUnitOfArea =
    fertilizationCostTotalsPerUnitOfProduction[0] -
    totalSavings -
    fertilizationCostTotalsPerUnitOfProduction[1] || 0;

  const finalCostDiff =
    fertilizationCostTotals[0] - totalSavings - fertilizationCostTotals[1] || 0;

  const finalTotalCosts = [
    fertilizationCostTotals[0] - totalSavings || 0,
    fertilizationCostTotals[1] || 0,
    finalCostDiff,
    fertilizationCostTotals[1] > 0
      ? roundToDecimals(finalCostDiff / fertilizationCostTotals[1]) * 100
      : 0 || 0,
    finalCostDiff * totalArea || 0,
  ];

  const finalCostsPerProductionUnit = [
    fertilizationCostTotalsPerUnitOfProduction[0] -
    indirectSavingsPerUnitOfProduction[0] || 0,
    fertilizationCostTotalsPerUnitOfProduction[1] -
    indirectSavingsPerUnitOfProduction[1] || 0,
    finalCostDiffPerUnitOfArea,
    haProductivity > 0
      ? roundToDecimals(finalCostDiffPerUnitOfArea / haProductivity) * 100
      : 0 || 0,
    finalCostDiffPerUnitOfArea * totalArea || 0,
  ];
  const finalPercents = [
    (finalTotalCosts[0] / totalProductionCost) * 100 || 0,
    (finalTotalCosts[1] / totalProductionCost) * 100 || 0,
    (finalTotalCosts[0] / totalProductionCost -
      finalTotalCosts[1] / totalProductionCost) *
    100 || 0,
  ];
  const UNIT = form[puid] || 'kg';

  const bottomRespostas = [
    [
      number(appliedProductTotals[0], { suffix: ' Kg/ha' }),
      number(appliedProductTotals[1], { suffix: ' Kg/ha' }),
      number(appliedProductTotals[0] - appliedProductTotals[1], {
        suffix: ' Kg/ha',
      }),
      number(
        ((appliedProductTotals[0] - appliedProductTotals[1]) /
          appliedProductTotals[1]) *
        100,
        {
          suffix: '%',
        },
      ),
      number((appliedProductTotals[0] - appliedProductTotals[1]) * totalArea, {
        suffix: ' Kg',
      }),
    ],
    [
      currency(fertilizationCostTotals[0], { suffix: ' /ha ' }),
      currency(fertilizationCostTotals[1], { suffix: ' /ha ' }),
      currency(fertilizationCostTotals[2], { suffix: ' /ha ' }),
      number(fertilizationCostTotals[3], { suffix: '%' }),
      currency(fertilizationCostTotals[2] * totalArea),
    ],
    [
      number(fertilizationCostTotalsPerUnitOfProduction[0], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
      number(fertilizationCostTotalsPerUnitOfProduction[1], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
      number(fertilizationCostTotalsPerUnitOfProduction[2], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
      number(
        (fertilizationCostTotalsPerUnitOfProduction[2] /
          fertilizationCostTotalsPerUnitOfProduction[1]) *
        100,
        { suffix: '%' },
      ),
      number(fertilizationCostTotalsPerUnitOfProduction[4], {
        suffix: ` ${UNIT}`,
        ed: 2,
      }),
    ],
    [
      currency(indirectSavings[0], { suffix: ' /ha ' }),
      currency(indirectSavings[1], { suffix: ' /ha ' }),
      currency(indirectSavings[2], { suffix: ' /ha ' }),
      number(indirectSavings[3], { suffix: '%' }),
      currency(indirectSavings[4]),
    ],
    [
      number(indirectSavingsPerUnitOfProduction[0], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
      number(indirectSavingsPerUnitOfProduction[1], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
      number(indirectSavingsPerUnitOfProduction[2], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
      number(indirectSavingsPerUnitOfProduction[3], { suffix: '%' }),
      number(indirectSavingsPerUnitOfProduction[4], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
    ],

    [
      currency(finalTotalCosts[0], { suffix: ' /ha' }),
      currency(finalTotalCosts[1], { suffix: ' /ha' }),
      currency(finalTotalCosts[0] - finalTotalCosts[1], { suffix: ' /ha' }),
      number(
        ((finalTotalCosts[0] - finalTotalCosts[1]) /
          (haProductivity * costPerUnitOfProduction)) *
        100,
        {
          suffix: '%',
        },
      ),
      currency(finalTotalCosts[4]),
    ],
    [
      number(finalCostsPerProductionUnit[0], { suffix: ` ${UNIT}/ha`, ed: 2 }),
      number(finalCostsPerProductionUnit[1], { suffix: ` ${UNIT}/ha`, ed: 2 }),
      number(finalCostsPerProductionUnit[0] - finalCostsPerProductionUnit[1], {
        suffix: ` ${UNIT}/ha`,
        ed: 2,
      }),
      number(
        ((finalCostsPerProductionUnit[0] - finalCostsPerProductionUnit[1]) /
          haProductivity) *
        100,
        { suffix: '%' },
      ),
      number(
        (finalCostsPerProductionUnit[0] - finalCostsPerProductionUnit[1]) *
        totalArea,
        { suffix: ` ${UNIT}`, ed: 2 },
      ),
    ],
    [
      number(finalPercents[0], { suffix: '%' }),
      number(finalPercents[1], { suffix: '%' }),
      '',
      number(finalPercents[2], { suffix: '%' }),
      '',
    ],
  ];

  const bottomPerguntas = [
    'Quantidade total de produto aplicado',
    'Custo de fertilização em R$/ha',
    `Custo de fertilização em ${UNIT}/ha`,
    'Economias indiretas em R$/ha',
    `Economias indiretas em ${UNIT}/ha`,
    'Expectativa mínima de produção com custo final em R$/ha',
    `Expectativa mínima de produção com custo final em ${UNIT}/ha`,
    '% sobre o Custo total de produção',
  ];

  return (
    <div className={ExpectativaMínima}>
      <Header title={'EXPECTATIVA MÍNIMA'} icon={faHandHoldingUsd}>
        <WithPermission permission='respostas.create'>
          <button
            id='editar'
            className={C([edit, Selected])}
            onClick={() => setEdit(!edit)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        </WithPermission>
        <PrintButton />
      </Header>
      {edit && (
        <button
          id='salvar'
          onClick={save}
          className={C(Saver, [!edit, Hidden])}
        >
          Salvar
        </button>
      )}
      <TopTable
        edit={edit}
        cultura={T(Cultura, { nome: '' }).nome}
        perguntas={Perguntas}
        respostas={Respostas}
        estados={Estados}
        form={form}
        setForm={setForm}
        productionUnit={UNIT}
        save={save}
      />
      {!edit && (
        <BottomTable perguntas={bottomPerguntas} respostas={bottomRespostas} />
      )}
    </div>
  );
}

ExpectativaMínima.propTypes = {
  match: PropTypes.any.isRequired,
};
